if (!customElements.get('age-gate')) {
  customElements.define(
    'age-gate',
    class AgeGate extends ModalComponent {
      get isLockingNeeded() {
        return true;
      }

      get requiresBodyAppended() {
        return true;
      }

      connectedCallback() {
        super.connectedCallback();

        this.ageForm = this.querySelector('.age-gate__form');
        this.errorEl = this.querySelector('.age-gate__error');
        this.errorText = this.querySelector('.age-gate__error-text');
        this.minAge = parseInt(this.dataset.minAge, 10) || 18;

        // Skip setup entirely if already verified this session
        if (this.isSessionVerified()) return;

        // Intercept form submissions that carry a checkout button as the submitter.
        // Listening on the `submit` event (not `click`) is the only guaranteed hook
        // that fires before the browser sends the request, regardless of how the
        // submission was triggered (click, Enter key, programmatic requestSubmit, etc.).
        document.addEventListener('submit', this.onCartSubmit.bind(this), true);

        // Handle the age-verification form inside this modal
        this.ageForm?.addEventListener('submit', this.onAgeFormSubmit.bind(this));

        // Clear error when any field changes
        this.ageForm?.addEventListener('change', () => this.clearError());
      }

      isSessionVerified() {
        try {
          return sessionStorage.getItem('age_verified') === 'true';
        } catch (_) {
          return false;
        }
      }

      // Capture checkout form submissions sitewide before they reach the browser
      onCartSubmit(event) {
        // Identify checkout submissions by the submitter button's name.
        // Falls back to querying inside the form for older browsers without SubmitEvent.submitter.
        const submitter = event.submitter || event.target.querySelector('[name="checkout"]');
        if (submitter?.name !== 'checkout') return;
        if (this.isSessionVerified()) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.pendingForm = event.target;
        this.pendingButton = submitter;

        this.show(submitter);
      }

      async onAgeFormSubmit(event) {
        event.preventDefault();

        const day = parseInt(this.ageForm.elements.day.value, 10);
        const month = parseInt(this.ageForm.elements.month.value, 10);
        const year = parseInt(this.ageForm.elements.year.value, 10);
        const currentYear = new Date().getFullYear();

        if (!day || !month || !year || year < 1900 || year > currentYear) {
          this.showError('Please enter a valid date of birth.');
          return;
        }

        const dob = new Date(year, month - 1, day);

        // Guard against invalid calendar dates (e.g. Feb 30)
        if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) {
          this.showError('Please enter a valid date of birth.');
          return;
        }

        if (this.calculateAge(dob) < this.minAge) {
          this.showError(
            this.dataset.errorUnderage ||
              `Sorry — you must be at least ${this.minAge} years old to purchase from this store.`
          );
          return;
        }

        const dobISO = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        try {
          sessionStorage.setItem('age_verified', 'true');
          sessionStorage.setItem('age_verified_dob', dobISO);
        } catch (_) {}

        // Fire-and-forget — do not block checkout on save
        this.saveVerification(dobISO);

        // Close modal then resume the intercepted checkout submission
        await this.hide();

        const form = this.pendingForm;
        const btn = this.pendingButton;
        this.pendingForm = null;
        this.pendingButton = null;

        if (form && btn) {
          // requestSubmit(btn) replays the submission with the original button as submitter,
          // so the checkout name/value is included in the POST body.
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(btn);
          } else {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = btn.name;
            input.value = btn.value || '';
            form.appendChild(input);
            form.submit();
          }
        } else {
          window.location.href = '/checkout';
        }
      }

      calculateAge(dob) {
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
        return age;
      }

      showError(message) {
        if (this.errorText) this.errorText.textContent = message;
        if (this.errorEl) this.errorEl.hidden = false;
      }

      clearError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      async saveVerification(dobISO) {
        // Always save as a cart attribute so the DOB is visible on every order
        try {
          await fetch(FoxTheme.routes.cart_update_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { age_verification_dob: dobISO } }),
          });
        } catch (_) {}

        // Attempt customer metafield save via Storefront API when a token is configured
        const storefrontToken = this.dataset.storefrontToken;
        if (!storefrontToken) return;

        let customerToken;
        try {
          customerToken =
            sessionStorage.getItem('storefront_customer_access_token') ||
            localStorage.getItem('storefront_customer_access_token');
        } catch (_) {}

        if (!customerToken) return;

        try {
          await fetch('/api/2024-01/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': storefrontToken,
            },
            body: JSON.stringify({
              query: `mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
                customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
                  customer { id }
                  customerUserErrors { field message }
                }
              }`,
              variables: {
                customerAccessToken: customerToken,
                customer: {
                  metafields: [
                    {
                      namespace: 'age_verification',
                      key: 'date_of_birth',
                      type: 'date',
                      value: dobISO,
                    },
                  ],
                },
              },
            }),
          });
        } catch (_) {}
      }

      // Prevent Escape key or overlay from dismissing an unverified gate
      hide() {
        if (window.Shopify?.designMode || this.isSessionVerified()) {
          return super.hide();
        }
      }
    }
  );
}
